<script lang="ts">
  import { MENU_COPY, CORP } from "@spacecrew/shared";
  import { audio } from "../audio/bus.js";
  import { radio } from "../intercom/radio.js";
  import { ship } from "../net/ship.svelte.js";
  import { leave, session } from "../net/session.svelte.js";
  import { send } from "../net/intents.js";
  import { menu } from "../lib/menu.svelte.js";

  const snap = $derived(ship.snap);
  const others = $derived(Object.entries(snap?.crew ?? {}).filter(([id]) => id !== session.sessionId));
  const votes = $derived(snap?.restartVotes ?? []);
  const signed = $derived(votes.includes(session.sessionId));
  const connected = $derived(Object.values(snap?.crew ?? {}).filter((c) => c.connected).length);
  const needed = $derived(Math.max(1, Math.ceil(connected / 2)));

  const BUSES = [
    { id: "master", label: "MASTER" },
    { id: "sfx", label: "SWITCHES & MACHINERY" },
    { id: "ambient", label: "SHIP AMBIENCE" },
    { id: "alert", label: "ALARMS & PA" },
    { id: "ui", label: "TERMINAL FEEDBACK" },
    { id: "intercom", label: "INTERCOM (ALL CREW)" },
    { id: "music", label: "GANGWAY MUSIC FEED" },
  ];
  // Sliders read from the audio bus once; the bus itself is the source of truth after that.
  let levels = $state(Object.fromEntries(BUSES.map((b) => [b.id, audio.level(b.id)])));
  let crewLevels = $state<Record<string, number>>({});
  $effect(() => { for (const [id] of others) crewLevels[id] ??= radio.level(id); });

  function setBus(id: string, v: number) { levels[id] = v; audio.unlock(); audio.setLevel(id, v); }
  function setCrew(id: string, v: number) { crewLevels[id] = v; radio.setLevel(id, v); }
  function onKey(e: KeyboardEvent) { if (e.key === "Escape") menu.open = false; }
</script>

<svelte:window on:keydown={onKey} />

<div class="scrim" role="presentation" onclick={() => (menu.open = false)}></div>
<div class="menu" role="dialog" aria-modal="true" aria-label={MENU_COPY.title}>
  <header>
    <h2>{CORP} — {MENU_COPY.title}</h2>
    <p class="dim">{MENU_COPY.form}</p>
  </header>

  <fieldset>
    <legend>{MENU_COPY.audio}</legend>
    {#each BUSES as bus}
      <label>
        <span>{bus.label}</span>
        <input type="range" min="0" max="1" step="0.02" value={levels[bus.id]}
          oninput={(e) => setBus(bus.id, Number((e.currentTarget as HTMLInputElement).value))} />
        <output>{Math.round((levels[bus.id] ?? 0) * 100)}</output>
      </label>
    {/each}
  </fieldset>

  {#if others.length}
    <fieldset>
      <legend>{MENU_COPY.crewAudio}</legend>
      {#each others as [id, c] (id)}
        <label>
          <span>{c.name}</span>
          <input type="range" min="0" max="1" step="0.02" value={crewLevels[id] ?? 1}
            oninput={(e) => setCrew(id, Number((e.currentTarget as HTMLInputElement).value))} />
          <output>{Math.round((crewLevels[id] ?? 1) * 100)}</output>
        </label>
      {/each}
    </fieldset>
  {/if}

  <fieldset>
    <legend>CONTRACT</legend>
    <button class="wide" aria-pressed={signed} onclick={() => send("mission.vote", {})}>
      {signed ? "WITHDRAW SIGNATURE" : MENU_COPY.restart} — {votes.length} / {needed} SIGNED
    </button>
    <p class="dim hint">{MENU_COPY.restartHint}</p>
  </fieldset>

  <div class="actions">
    <button onclick={() => (menu.open = false)}>{MENU_COPY.close}</button>
    <button class="danger" onclick={() => { menu.open = false; void leave(); }}>{MENU_COPY.leave}</button>
  </div>
</div>

<style>
  .scrim { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.72); z-index: 40; }
  .menu {
    position: fixed; z-index: 41; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: min(560px, 92vw); max-height: 88dvh; overflow-y: auto;
    background: #05100a; border: 1px solid var(--fg-dim); padding: 1.1rem 1.3rem;
    display: grid; gap: 1rem; box-shadow: 0 0 60px rgba(0, 0, 0, 0.9), inset 0 0 40px rgba(0, 0, 0, 0.6);
  }
  header h2 { margin: 0; }
  header p { margin: 0.2rem 0 0; font-size: 0.8em; }
  fieldset { border: 1px solid var(--fg-dim); padding: 0.6rem 0.8rem 0.8rem; display: grid; gap: 0.4rem; }
  legend { padding: 0 0.4em; font-size: 0.8em; color: var(--fg-dim); text-transform: uppercase; letter-spacing: 0.08em; }
  label { display: grid; grid-template-columns: 14ch 1fr 3ch; align-items: center; gap: 0.6rem; font-size: 0.85em; }
  input[type="range"] { width: 100%; accent-color: var(--fg); }
  output { text-align: right; color: var(--fg-dim); font-variant-numeric: tabular-nums; }
  .wide { width: 100%; }
  .hint { font-size: 0.75em; margin: 0.2rem 0 0; }
  .actions { display: flex; gap: 0.6rem; justify-content: space-between; }
  .danger { border-color: var(--bad); color: var(--bad); }
</style>
